import React, { useState } from "react";
import { useForm } from "react-hook-form";

import { CancelButton, FormButton } from "../../../../../components/Button";
import {
  DocUploader,
  FormFieldset,
  Input,
  ViewTag,
} from "../../../../../components/Form";
import ImageUploader from "../../../../../components/ImageUploader";
import Loader from "../../../../../components/Loader";
import { IAddEmployee, IEmployeeId } from "../../Employee.props";
import useEditEmployee from "./useEditEmployee";
import Eyes from "../../../../../images/SideBar/Eyes";
import ShowEye from "../../../../../images/SideBar/ShowEye";

type ArrayField =
  | "leave"
  | "employeeCareer"
  | "employeeQualification"
  | "familyDetail";

type FormField = {
  label: string;
  name: keyof IAddEmployee;
  type: string;
};

type DocumentField = {
  label: string;
  name: keyof NonNullable<IAddEmployee["documentsSubmitted"]>;
};

const EditEmployee: React.FC<IEmployeeId> = ({ employeeId, setMethod }) => {
  const {
    loading,
    formData,
    image,
    onDrop,
    removeImage,
    handleChange,
    handlePasswordChange,
    handleArrayChange,
    addArrayItem,
    removeArrayItem,
    handleSubmit,
    handleExcelUpload,
    documents,
    newPassword,
    setNewPassword,
    handleSlackConnect,
  } = useEditEmployee(employeeId, setMethod);

  const { handleSubmit: useFormSubmit } = useForm<IAddEmployee>();

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const personalFields: FormField[] = [
    { label: "Full Name", name: "name", type: "text" },
    { label: "User Email", name: "userEmail", type: "email" },
    { label: "Company Email Id", name: "email", type: "email" },
    { label: "Sub Role", name: "subRole", type: "text" },
    { label: "Employee ID", name: "empId", type: "text" },
    { label: "Date of Joining", name: "doj", type: "date" },
    { label: "Designation", name: "designation", type: "text" },
    { label: "Department", name: "department", type: "text" },
    { label: "Phone", name: "phone", type: "tel" },
    { label: "Emergency Contact", name: "emergencyContact", type: "tel" },
    { label: "Aadhar Card Number", name: "adharCardNumber", type: "text" },
    { label: "PAN Card Number", name: "panCardNumber", type: "text" },
    { label: "Password", name: "password", type: "password" },
  ];

  const addressFields: FormField[] = [
    { label: "Current Address", name: "currentAddress", type: "text" },
    { label: "Permanent Address", name: "permanentAddress", type: "text" },
  ];

  const bankFields: FormField[] = [
    { label: "Bank Account Number", name: "bankAccountNo", type: "text" },
    { label: "Bank Name", name: "bankName", type: "text" },
    { label: "Bank IFSC", name: "bankIFSC", type: "text" },
    { label: "Bank Branch", name: "bankBranch", type: "text" },
  ];

  const documentFields: DocumentField[] = [
    { label: "Offer Letter", name: "offerLetter" },
    { label: "Payslip", name: "payslip" },
    { label: "Relieving Letter", name: "relievingLetter" },
    { label: "PAN Card Copy", name: "panCardCopy" },
    { label: "Aadhar Card Copy", name: "adharCardCopy" },
    { label: "Education Proof", name: "educationProof" },
    { label: "Degree Copy", name: "degreeCopy" },
    { label: "Experience Certificates", name: "experienceCertificates" },
    { label: "Marksheets", name: "marksheets" },
    { label: "Photo Copy", name: "photoCopy" },
    { label: "Cheque Copy", name: "chequeCopy" },
  ];


  const renderArrayTable = (
    field: ArrayField,
    headers: string[],
    template: any
  ) => {
    const items = formData[field];
    console.log("items", items);
    return (
      <div className="mt-4">
        <h3 className="text-lg font-semibold mb-2">{field}</h3>
        <table className="min-w-full">
          <thead>
            <tr>
              {headers.map((header) => (
                <th key={header} className="px-4 py-2 border">
                  {header}
                </th>
              ))}
              <th className="px-4 py-2 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item: any, index: number) => (
              <tr key={index}>
                {Object.keys(template).map((key) => (
                  <td key={key} className="px-4 py-2 border">
                    <input
                      type={key.includes("Date") ? "date" : "text"}
                      value={
                        key.includes("Date") && item[key]
                          ? new Date(item[key]).toISOString().split("T")[0]
                          : item[key] || ""
                      }
                      onChange={(e) =>
                        handleArrayChange(field, index, key, e.target.value)
                      }
                      className="w-full p-1 border rounded"
                    />
                  </td>
                ))}
                <td className="px-4 py-2 border">
                  <button
                    type="button"
                    onClick={() => removeArrayItem(field, index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          type="button"
          onClick={() => addArrayItem(field, { ...template })}
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Add {field}
        </button>
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center w-full h-full pr-8 pl-6">
      <div className="bg-white shadow-md rounded-lg pt-6 pb-6 w-full">
        <div className="border-b-2 w-[100%] flex justify-between items-center pr-5">
          <h1 className="text-3xl mb-2 text-fontBlack ml-5">Edit Employee</h1>
          <div className="flex items-center">
            <label className="cursor-pointer rounded-lg text-base font-bold bg-iconBg text-white py-2 px-4">
              Import Excel
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleExcelUpload}
                className="hidden"
              />
            </label>
          </div>
        </div>
        {loading && <Loader />}
        <form
          onSubmit={useFormSubmit(handleSubmit)}
          className="space-y-6 p-6 h-[75vh] overflow-y-scroll"
        >
          <FormFieldset Ftitle="Personal details">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 m-4">
              {personalFields.map((field, index) => (
                <>
                  <div key={field.name}>
                    {field.name === "password" ? (
                      <button
                        type="button"
                        onClick={() => setShowPasswordModal(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md absolute top-20 right-18"
                      >
                        Password
                      </button>
                    ) : (
                      <Input
                        name={field.name}
                        label={field.label}
                        type={field.type}
                        value={
                          field.name === "doj"
                            ? (formData[field.name] as string)?.split("T")[0] ||
                              ""
                            : (formData[field.name] as string)
                        }
                        onChange={handleChange}
                        required
                        disabled={field.name === "email"}
                      />
                    )}
                  </div>
                  {showPasswordModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-5 flex justify-center items-center z-50">
                      <div className="bg-white p-6 rounded-lg shadow-md w-96">
                        <h2 className="text-lg font-semibold mb-4">
                          Change Password
                        </h2>
                        <div className="relative w-full">
                          <Input
                            name="password"
                            label="New Password"
                            type='text'
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                          />
                         
                        </div>
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => setShowPasswordModal(false)}
                            className="px-4 py-2 rounded-md border border-gray-300"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              handlePasswordChange();
                              setShowPasswordModal(false);
                            }}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ))}
            </div>
          </FormFieldset>

          <FormFieldset Ftitle="Slack details">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 m-4 items-center">
              <Input
                name="slackId"
                label="Slack Id"
                type="text"
                value={formData.slackId as string}
                onChange={handleChange}
              />
              <Input
                name="slackChannelId"
                label="Slack Channel Id"
                type="text"
                value={formData.slackChannelId as string}
                onChange={handleChange}
                disabled
              />
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={handleSlackConnect}
                  disabled={loading || !formData.slackId}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed h-[56px] w-full"
                >
                  Connect
                </button>
              </div>
            </div>
          </FormFieldset>

          <FormFieldset Ftitle="Address details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 m-4">
              {addressFields.map((field) => (
                <Input
                  key={field.name}
                  name={field.name}
                  label={field.label}
                  type={field.type}
                  value={formData[field.name] as string}
                  onChange={handleChange}
                />
              ))}
            </div>
          </FormFieldset>

          <FormFieldset Ftitle="Bank details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 m-4">
              {bankFields.map((field) => (
                <Input
                  key={field.name}
                  name={field.name}
                  label={field.label}
                  type={field.type}
                  value={formData[field.name] as string}
                  onChange={handleChange}
                />
              ))}
            </div>
          </FormFieldset>

          <FormFieldset Ftitle="Documents submitted">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 m-4">
              {documentFields.map((field) => (
                <div key={field.name} className="flex items-center">
                  <input
                    type="checkbox"
                    id={field.name}
                    name={field.name}
                    checked={formData.documentsSubmitted[field.name]}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <label htmlFor={field.name}>{field.label}</label>
                </div>
              ))}
            </div>
          </FormFieldset>

          <FormFieldset Ftitle="Previous Company">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 m-4">
              <Input
                name="companyName"
                label="Company Name"
                type="text"
                value={formData.previousCompany.companyName}
                onChange={(e) => {
                  handleChange({
                    target: {
                      id: "previousCompany",
                      value: {
                        ...formData.previousCompany,
                        companyName: e.target.value,
                      },
                    },
                  });
                }}
              />
              <Input
                name="HREmailAddress"
                label="HR Email Address"
                type="email"
                value={formData.previousCompany.HREmailAddress}
                onChange={(e) => {
                  handleChange({
                    target: {
                      id: "previousCompany",
                      value: {
                        ...formData.previousCompany,
                        HREmailAddress: e.target.value,
                      },
                    },
                  });
                }}
              />
              <Input
                name="linkedinProfile"
                label="LinkedIn Profile"
                type="text"
                value={formData.previousCompany.linkedinProfile}
                onChange={(e) => {
                  handleChange({
                    target: {
                      id: "previousCompany",
                      value: {
                        ...formData.previousCompany,
                        linkedinProfile: e.target.value,
                      },
                    },
                  });
                }}
              />
            </div>
          </FormFieldset>

          <FormFieldset Ftitle="Employee Career">
            {renderArrayTable(
              "employeeCareer",
              [
                "Company",
                "City",
                "Start Date",
                "End Date",
                "Joining Salary",
                "Last Salary",
                "Remarks",
                "Duration",
              ],
              {
                company: "",
                city: "",
                startDate: "",
                endDate: "",
                joiningSalary: "",
                lastSalary: "",
                remarks: "",
                duration: "",
              }
            )}
          </FormFieldset>

          <FormFieldset Ftitle="Employee Qualification">
            {renderArrayTable(
              "employeeQualification",
              [
                "Degree",
                "College",
                "Pass Year",
                "University",
                "Grade",
                "Remarks",
              ],
              {
                degree: "",
                college: "",
                passYear: "",
                university: "",
                grade: "",
                remarks: "",
              }
            )}
          </FormFieldset>

          <FormFieldset Ftitle="Family Details">
            {renderArrayTable(
              "familyDetail",
              ["Relation", "Name", "Industry", "Contact No"],
              {
                Relation: "",
                Name: "",
                Industry: "",
                ContactNo: "",
              }
            )}
          </FormFieldset>

          <FormFieldset Ftitle="Academics">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 m-4">
              <Input
                name="stream"
                label="Stream"
                type="text"
                value={formData.academics.stream}
                onChange={(e) => {
                  handleChange({
                    target: {
                      id: "academics",
                      value: { ...formData.academics, stream: e.target.value },
                    },
                  });
                }}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 m-4">
              <h4 className="col-span-3 font-semibold">10th Details</h4>
              <Input
                name="tenthMaths"
                label="Maths"
                type="number"
                value={formData.academics.tenth.maths}
                onChange={(e) => {
                  handleChange({
                    target: {
                      id: "academics",
                      value: {
                        ...formData.academics,
                        tenth: {
                          ...formData.academics.tenth,
                          maths: Number(e.target.value),
                        },
                      },
                    },
                  });
                }}
              />
              <Input
                name="tenthScience"
                label="Science"
                type="number"
                value={formData.academics.tenth.science}
                onChange={(e) => {
                  handleChange({
                    target: {
                      id: "academics",
                      value: {
                        ...formData.academics,
                        tenth: {
                          ...formData.academics.tenth,
                          science: Number(e.target.value),
                        },
                      },
                    },
                  });
                }}
              />
              <Input
                name="tenthPercentage"
                label="Percentage"
                type="number"
                value={formData.academics.tenth.percentage}
                onChange={(e) => {
                  handleChange({
                    target: {
                      id: "academics",
                      value: {
                        ...formData.academics,
                        tenth: {
                          ...formData.academics.tenth,
                          percentage: Number(e.target.value),
                        },
                      },
                    },
                  });
                }}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 m-4">
              <h4 className="col-span-3 font-semibold">12th Details</h4>
              <Input
                name="twelfthMaths"
                label="Maths"
                type="number"
                value={formData.academics.twelfth.maths}
                onChange={(e) => {
                  handleChange({
                    target: {
                      id: "academics",
                      value: {
                        ...formData.academics,
                        twelfth: {
                          ...formData.academics.twelfth,
                          maths: Number(e.target.value),
                        },
                      },
                    },
                  });
                }}
              />
              <Input
                name="twelfthPhysics"
                label="Physics"
                type="number"
                value={formData.academics.twelfth.physics}
                onChange={(e) => {
                  handleChange({
                    target: {
                      id: "academics",
                      value: {
                        ...formData.academics,
                        twelfth: {
                          ...formData.academics.twelfth,
                          physics: Number(e.target.value),
                        },
                      },
                    },
                  });
                }}
              />
              <Input
                name="twelfthPercentage"
                label="Percentage"
                type="number"
                value={formData.academics.twelfth.percentage}
                onChange={(e) => {
                  handleChange({
                    target: {
                      id: "academics",
                      value: {
                        ...formData.academics,
                        twelfth: {
                          ...formData.academics.twelfth,
                          percentage: Number(e.target.value),
                        },
                      },
                    },
                  });
                }}
              />
            </div>
          </FormFieldset>

          <div className="flex justify-center mt-6 ml-2 gap-3">
            <CancelButton
              className="py-2 px-4 h-[56px] w-[200.5px]"
              onClick={() => setMethod?.()}
              children="Cancel"
              type="button"
            />
            <FormButton
              className="py-2 px-4 h-[56px] w-[200.5px]"
              type="submit"
              children="UPDATE"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEmployee;
